import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, toolTransform } from '@backrs_ui/common-ui';

import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

import { Box, SxProps, Theme } from '@mui/material';

import { useRecommendedToolsAgentContext } from './RecommendedTools.agentContext';

// Curated by the product team — tools that help a student act on their score plan, by tool
// id (same ids the Tools page uses). Only /tools-backed ids render here; competencies do
// not, since each card renders once its backing tool loads from the API.
const RECOMMENDED_TOOL_IDS: readonly StickerCardId[] = [
  'what_are_your_chances',
  'are_you_on_track',
  'blueprint',
  'life_readiness',
];

/** "Recommended Tools & Resources" card row: a horizontal row of curated Backrs tools. */
export const RecommendedTools = () => {
  const navigate = useNavigate();
  const { getStickerCard } = useToolCardById({ navigate });

  // Resolve each id to its sticker card, keep only those backed by a live tool.
  const cards = useMemo(
    () => RECOMMENDED_TOOL_IDS.map((id) => getStickerCard(id)).filter((card) => !!card.tool),
    [getStickerCard],
  );

  // Present the resolved cards in the shared nugget-card shape (as the Tools page does).
  const nuggets = useMemo(
    () => cards.flatMap((card) => (card.tool ? [toolTransform(card.tool)] : [])),
    [cards],
  );

  const toolContext = useMemo(
    () =>
      cards.map((card) => ({
        title: card.tool?.title ?? card.title,
        description: card.tool?.internal_description ?? card.tool?.description ?? card.description,
        keywords: card.tool?.keywords ?? card.keywords,
      })),
    [cards],
  );

  useRecommendedToolsAgentContext(toolContext);

  if (nuggets.length === 0) return null;

  return (
    <Box sx={rootSx} id="booster-recommended-tools">
      <NuggetCardList
        nuggets={nuggets}
        onClick={(nugget: ContentNugget) => handlePanelNuggetClick(nugget, navigate)}
        direction="scrollingRow"
        hideActions
      />
    </Box>
  );
};

const rootSx: SxProps<Theme> = {
  width: '100%',
};
